import { useQuery, useMutation } from '@apollo/client';
import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { CreditCard, AlertCircle, Lock, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import {
  GET_USER_ACCOUNTS,
  GET_ACCOUNT_BILLING_DETAILS,
  GET_ACCOUNT_DETAILS,
  GET_USER_PROFILE,
} from '../../graphql';
import {
  BILLING_PORTAL_URL,
  CREATE_SETUP_INTENT,
  ADD_PAYMENT_METHOD,
  REMOVE_PAYMENT_METHOD,
  MARK_PAYMENT_METHOD_DEFAULT,
  UPDATE_PRODUCT_PLAN,
  CANCEL_SUBSCRIPTION,
} from '../../graphql/mutations';
import {
  GetUserAccountsResponse,
  GetAccountBillingDetailsResponse,
  GetAccountDetailsResponse,
} from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';
import { useProduct } from '../../context/ProductContext';
import {
  BillingInterval,
  money,
  planPrice,
  toInterval,
  useTinycmsPlans,
} from '../account/tinycmsPlans';
import { TinycmsChangePlan } from './TinycmsChangePlan';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Loader } from '../common/PageLoader';

const stripePromise: Promise<Stripe | null> = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);

interface UserProfileData {
  me?: {
    id: string;
    email: string;
  };
}

// Plan configuration mapping
const PLAN_CONFIG: Record<string, { price: string; users: string }> = {
  Enterprise: { price: '$149.00', users: '10' },
  Pro: { price: '$29.00', users: '3' },
  Basic: { price: '$9.00', users: '1' },
};

function pricePerMonth(value: number | string | undefined) {
  if (value == null || value === '') return '—';
  const amount = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(amount)) return '—';
  const shown = Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
  return `${shown} / mo`;
}

// Pricing plans (matching SignUp.tsx)
interface PricingPlan {
  id: string;
  name: string;
  price: number;
  description: string;
  features: string[];
  popular?: boolean;
}

const pricingPlans: PricingPlan[] = [
  {
    id: 'basic',
    name: 'Basic',
    price: 9,
    description: 'Perfect for individuals',
    features: [
      'Up to 2 social profiles',
      'Up to 100 posts per month',
      'Basic analytics',
      'Email support',
      '14-day free trial',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 29,
    description: 'For professionals and small teams',
    features: [
      'Unlimited 10 social profiles',
      'Up to 1000 posts per month',
      'Advanced analytics',
      'Priority support',
      'Team collaboration',
    ],
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 149,
    description: 'For large organizations',
    features: [
      'Everything in Pro',
      'Unlimited social profiles',
      'Unlimited posts per month',
      'Manage multiple organizations',
      'SSO integration',
      'Custom 3rd party integrations',
      'Dedicated account manager',
      'SLA guarantee',
    ],
  },
];

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#f2efe0',
      '::placeholder': {
        color: '#9ca3af',
      },
      fontFamily: 'system-ui, sans-serif',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
  hidePostalCode: true,
};

interface SetupIntentResponse {
  setupIntent: {
    clientSecret: string;
  };
}

interface AddPaymentMethodResponse {
  updatePaymentMethod: {
    cardLast4: string;
    cardExpMonth: number;
    cardExpYear: number;
    isDefault: boolean;
    isRemovable: boolean;
  };
}

interface PaymentFormProps {
  accountId: string;
  userEmail?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

function PaymentForm({ accountId, userEmail, onSuccess, onCancel }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [errors, setErrors] = useState<{ card?: string; submit?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoadingSetupIntent, setIsLoadingSetupIntent] = useState<boolean>(true);

  const [createSetupIntent] = useMutation<SetupIntentResponse>(CREATE_SETUP_INTENT);
  const [addPaymentMethod] = useMutation<AddPaymentMethodResponse>(ADD_PAYMENT_METHOD);

  useEffect(() => {
    const fetchSetupIntent = async () => {
      try {
        const { data } = await createSetupIntent();
        if (data?.setupIntent?.clientSecret) {
          setClientSecret(data.setupIntent.clientSecret);
        }
      } catch (error) {
        console.error('Failed to create setup intent:', error);
        setErrors({ submit: 'Failed to initialize payment form. Please try again.' });
      } finally {
        setIsLoadingSetupIntent(false);
      }
    };

    fetchSetupIntent();
  }, [createSetupIntent]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement || !clientSecret) {
        setErrors({ submit: 'Payment form not ready. Please try again.' });
        setIsSubmitting(false);
        return;
      }

      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: userEmail
            ? {
                email: userEmail,
              }
            : undefined,
        },
      });

      if (stripeError) {
        setErrors({ submit: stripeError.message || 'Payment setup failed. Please try again.' });
        setIsSubmitting(false);
        return;
      }

      if (setupIntent && setupIntent.status === 'succeeded' && setupIntent.payment_method) {
        const { data } = await addPaymentMethod({
          variables: {
            accountId: accountId,
            paymentMethod: setupIntent.payment_method as string,
          },
        });

        if (data?.updatePaymentMethod) {
          onSuccess();
        }
      }
    } catch (error) {
      console.error('Add payment method failed:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to add payment method. Please try again.';
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingSetupIntent) {
    return (
      <div className="py-8">
        <Loader label="Loading payment form" className="text-center text-muted-foreground" />
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="py-8">
        <p className="text-center text-destructive">
          {errors.submit || 'Failed to initialize payment form. Please try again.'}
        </p>
      </div>
    );
  }

  const isTestMode =
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.startsWith('pk_test_') ||
    !process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-4">
        {/* Test Mode Banner */}
        {isTestMode && (
          <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-yellow-500">Test Mode</p>
              <p className="text-xs text-yellow-400/80 mt-1">
                Use test card: 4242 4242 4242 4242, any future expiry date, and any 3-digit CVC
              </p>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="card-element">Card Details</Label>
          <div className="p-3 bg-form-dark rounded border border-cmp-line-2">
            <CardElement id="card-element" options={cardElementOptions} />
          </div>
          {errors.card && <p className="text-destructive text-sm">{errors.card}</p>}
        </div>

        <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
          <Lock className="w-4 h-4 text-muted-foreground" />
          <p className="text-muted-foreground text-sm">
            Your payment information is encrypted and secure
          </p>
        </div>
        {errors.submit && <p className="text-destructive text-sm">{errors.submit}</p>}
      </div>
      <DialogFooter className="mt-6">
        <Button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="bg-button-secondary text-button-secondary"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting || !stripe}
          className="bg-button-primary text-button-primary"
        >
          {isSubmitting ? 'Saving...' : 'Save Payment Method'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function BillingSettings() {
  const [isAddPaymentMethodOpen, setIsAddPaymentMethodOpen] = useState(false);
  const [isChangePlanOpen, setIsChangePlanOpen] = useState(false);
  const [isCancelSubscriptionOpen, setIsCancelSubscriptionOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  // TinyCMS shell: the Free / Personal / Business / Pro plans, monthly or yearly.
  const { product } = useProduct();
  const isTinycms = product === 'atomizer';
  const { plans: tinycmsPlans } = useTinycmsPlans();
  const [planInterval, setPlanInterval] = useState<BillingInterval>('year');
  const [planError, setPlanError] = useState('');

  const {
    accountId,
    selectedAccount: organization,
    isLoading: loading,
    error,
    refetchAccounts,
  } = useAccount();
  console.log('Selected account for billing:', organization);

  const [removePaymentMethod, { loading: isRemoving }] = useMutation(REMOVE_PAYMENT_METHOD);
  const [markPaymentMethodDefault, { loading: isSettingDefault }] = useMutation(
    MARK_PAYMENT_METHOD_DEFAULT
  );
  const [updateProductPlan, { loading: isUpdatingPlan }] = useMutation(UPDATE_PRODUCT_PLAN);
  const [cancelSubscription, { loading: isCancelingSubscription }] =
    useMutation(CANCEL_SUBSCRIPTION);

  // Get account details including activeOrder for plan, monthly price, and next billing date
  const {
    loading: accountDetailsLoading,
    data: accountDetailsData,
    refetch: refetchAccountDetails,
  } = useQuery<GetAccountDetailsResponse>(GET_ACCOUNT_DETAILS, {
    variables: { id: accountId },
    skip: !accountId,
    onCompleted: (data) => {
      console.log('Account Details Query Completed:', data);
    },
    onError: (error) => {
      console.error('Account Details Query Error:', error);
    },
  });

  const { data: billingData, refetch: refetchBilling } = useQuery<GetAccountBillingDetailsResponse>(
    GET_ACCOUNT_BILLING_DETAILS,
    {
      variables: { accountId: accountId },
      skip: !accountId,
      onCompleted: (data) => {
        console.log('Billing Details Query Completed:', data);
      },
      onError: (error) => {
        console.error('Billing Details Query Error:', error);
      },
    }
  );

  // Get user email for billing details
  const { data: userProfileData } = useQuery<UserProfileData>(GET_USER_PROFILE);

  const [openPortal, { loading: openingPortal }] = useMutation<{ billingPortalUrl: string }>(
    BILLING_PORTAL_URL
  );
  const [portalError, setPortalError] = useState('');
  const goToPortal = async () => {
    setPortalError('');
    try {
      const { data } = await openPortal({ variables: { accountId } });
      if (data?.billingPortalUrl) window.location.assign(data.billingPortalUrl);
    } catch (err) {
      setPortalError(err instanceof Error ? err.message : 'That didn’t open. Try again.');
    }
  };

  // Get plan, monthly price, and next billing date from account details
  const accountDetails = accountDetailsData?.account;
  const currentPlan = accountDetails?.plan || organization?.plan || '';
  const activeOrder = accountDetails?.activeOrder;

  console.log('Active Order:', activeOrder);
  const monthlyPrice = activeOrder?.monthlyRate
    ? pricePerMonth(activeOrder.monthlyRate)
    : pricePerMonth(PLAN_CONFIG[currentPlan]?.price);

  // The Stripe subscription, as last synced (status "grandfathered": a paid
  // plan from before billing, not charged).
  const billing = accountDetails?.billing || null;
  const billingStatus = billing?.status || 'none';
  const isTrialing = billingStatus === 'trialing';
  const isPastDue = billingStatus === 'past_due';
  const isGrandfathered = billingStatus === 'grandfathered';
  // Check if subscription is cancelled (ends at the period's end)
  const isCancelled = Boolean(billing?.cancelAtPeriodEnd) || !!activeOrder?.orderCancelDate;

  // Format date helper
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return date.getFullYear() === new Date().getFullYear() ? day : `${day}, ${date.getFullYear()}`;
  };

  // Calculate active until date (one day before orderNextBillDate)
  const getActiveUntilDate = () => {
    if (!activeOrder?.orderNextBillDate) return '—';
    const nextBillDate = new Date(activeOrder.orderNextBillDate);
    const activeUntil = new Date(nextBillDate);
    activeUntil.setDate(activeUntil.getDate() - 1);
    return formatDate(activeUntil.toISOString());
  };

  const periodEndDate = billing?.periodEnd || activeOrder?.orderNextBillDate;
  const nextBillingDate = isCancelled
    ? formatDate(billing?.periodEnd || activeOrder?.orderCancelDate)
    : formatDate(periodEndDate);

  const activeUntilDate = isCancelled
    ? billing?.periodEnd
      ? formatDate(billing.periodEnd)
      : getActiveUntilDate()
    : null;

  const tinycmsPlan = isTinycms
    ? tinycmsPlans.find((p) => p.key === (activeOrder?.plan || currentPlan).toLowerCase())
    : undefined;
  const orderInterval = toInterval(activeOrder?.billingInterval || 'month');
  const planLabel = tinycmsPlan?.label || currentPlan;
  const tinycmsPrice = tinycmsPlan ? planPrice(tinycmsPlan, orderInterval) : 0;
  const priceLabel = tinycmsPlan
    ? tinycmsPrice
      ? `${money(tinycmsPrice)} / mo`
      : 'Free'
    : monthlyPrice;
  // Under the price: how it's billed ("$216 billed yearly").
  const billingCycle =
    tinycmsPlan && tinycmsPrice
      ? orderInterval === 'year'
        ? `${money(tinycmsPrice * 12)} billed yearly`
        : 'Billed monthly'
      : '';
  // A Free plan has no bill to show or subscription to cancel.
  const isFreePlan = Boolean(tinycmsPlan) && !Number(activeOrder?.monthlyRate);
  const planConfig = tinycmsPlan
    ? { price: priceLabel, users: String(tinycmsPlan.maxMembers) }
    : PLAN_CONFIG[currentPlan] || { price: monthlyPrice, users: 'N/A' };

  const handleAddPaymentMethodSuccess = () => {
    setIsAddPaymentMethodOpen(false);
    // Refetch billing details to show the new payment method
    refetchBilling();
  };

  const handleRemovePaymentMethod = async (paymentMethodId: string) => {
    if (!accountId) {
      console.error('Account ID is required to remove payment method');
      return;
    }

    try {
      await removePaymentMethod({
        variables: {
          accountId: accountId,
          paymentMethod: paymentMethodId,
        },
        update: (cache) => {
          // Read the current billing details from cache
          const existingData = cache.readQuery<GetAccountBillingDetailsResponse>({
            query: GET_ACCOUNT_BILLING_DETAILS,
            variables: { accountId: accountId },
          });

          if (existingData?.billingDetails) {
            // Filter out the removed payment method
            const updatedBillingDetails = existingData.billingDetails.filter(
              (detail) => detail.id !== paymentMethodId
            );

            // Write the updated data back to cache
            cache.writeQuery({
              query: GET_ACCOUNT_BILLING_DETAILS,
              variables: { accountId: accountId },
              data: {
                billingDetails: updatedBillingDetails,
              },
            });
          }
        },
      });
    } catch (error) {
      console.error('Failed to remove payment method:', error);
      // You might want to show an error toast/notification here
      // On error, refetch to ensure UI is in sync with server
      refetchBilling();
    }
  };

  const handleSetDefaultPaymentMethod = async (paymentMethodId: string) => {
    if (!accountId) {
      console.error('Account ID is required to set default payment method');
      return;
    }

    try {
      await markPaymentMethodDefault({
        variables: {
          accountId: accountId,
          paymentMethod: paymentMethodId,
        },
        update: (cache) => {
          // Read the current billing details from cache
          const existingData = cache.readQuery<GetAccountBillingDetailsResponse>({
            query: GET_ACCOUNT_BILLING_DETAILS,
            variables: { accountId: accountId },
          });

          if (existingData?.billingDetails) {
            // Update all payment methods: set the selected one as default, others as not default
            const updatedBillingDetails = existingData.billingDetails.map((detail) => ({
              ...detail,
              isDefault: detail.id === paymentMethodId,
            }));

            // Write the updated data back to cache
            cache.writeQuery({
              query: GET_ACCOUNT_BILLING_DETAILS,
              variables: { accountId: accountId },
              data: {
                billingDetails: updatedBillingDetails,
              },
            });
          }
        },
      });
    } catch (error) {
      console.error('Failed to set default payment method:', error);
      // On error, refetch to ensure UI is in sync with server
      refetchBilling();
    }
  };

  const handleOpenChangePlan = () => {
    setPlanError('');
    if (isTinycms) {
      setSelectedPlan(tinycmsPlan?.key || 'personal');
      setPlanInterval(tinycmsPlan ? orderInterval : 'year');
      setIsChangePlanOpen(true);
      return;
    }
    // Set the current plan as selected when opening the modal
    // Convert current plan (e.g., "Pro", "Basic") to plan ID (e.g., "pro", "basic")
    const currentPlanId = currentPlan.toLowerCase();
    // Check if the plan ID exists in pricingPlans, otherwise default to 'basic'
    const planExists = pricingPlans.some((plan) => plan.id === currentPlanId);
    setSelectedPlan(planExists ? currentPlanId : 'basic');
    setIsChangePlanOpen(true);
  };

  /** TinyCMS: the plan and billing chosen in Change plan, applied now. */
  const handleSwitchTinycmsPlan = async (planKey: string, interval: BillingInterval) => {
    if (!accountId) return;
    setPlanError('');
    try {
      await updateProductPlan({
        variables: {
          accountId,
          productPlan: planKey,
          billingInterval: interval,
          product: 'tinycms',
        },
      });
      setIsChangePlanOpen(false);
      // The plan can change the account's products (Pro adds Atomizer and Reach).
      refetchAccounts();
      refetchAccountDetails();
    } catch (err) {
      setPlanError(err instanceof Error ? err.message : 'Your plan couldn’t be changed.');
    }
  };

  const handleSavePlan = async () => {
    if (!accountId || !selectedPlan) {
      console.error('Account ID and selected plan are required');
      return;
    }
    if (isTinycms) {
      setPlanError('');
      try {
        await updateProductPlan({
          variables: {
            accountId,
            productPlan: selectedPlan,
            billingInterval: planInterval,
            product: 'tinycms',
          },
        });
        setIsChangePlanOpen(false);
        // The plan can change the account's products (Pro adds Atomizer and Reach).
        refetchAccounts();
        refetchAccountDetails();
      } catch (err) {
        setPlanError(err instanceof Error ? err.message : 'Could not change the plan');
      }
      return;
    }

    try {
      // Find the plan details to get the proper name
      const planDetails = pricingPlans.find((plan) => plan.id === selectedPlan);
      if (!planDetails) {
        console.error('Selected plan not found');
        return;
      }

      // Use the plan name from pricingPlans (e.g., "Pro", "Basic", "Enterprise")
      const planName = planDetails.name;

      await updateProductPlan({
        variables: {
          accountId: accountId,
          productPlan: planName,
        },
        update: (cache) => {
          // Update the user accounts cache with the new plan
          const existingData = cache.readQuery<GetUserAccountsResponse>({
            query: GET_USER_ACCOUNTS,
          });

          if (existingData?.userAccounts) {
            const updatedAccounts = existingData.userAccounts.map((account) =>
              account.id === accountId ? { ...account, plan: planName } : account
            );

            cache.writeQuery({
              query: GET_USER_ACCOUNTS,
              data: {
                userAccounts: updatedAccounts,
              },
            });
          }
        },
      });

      setIsChangePlanOpen(false);
      // Refetch accounts and account details to ensure UI is in sync
      refetchAccounts();
      refetchAccountDetails();
    } catch (error) {
      console.error('Failed to update plan:', error);
      // You might want to show an error toast/notification here
    }
  };

  const handleCancelSubscription = async () => {
    if (!accountId) {
      console.error('Account ID is required to cancel subscription');
      return;
    }

    try {
      // The site stays active: the plan runs to the end of the paid period.
      await cancelSubscription({ variables: { accountId } });

      setIsCancelSubscriptionOpen(false);
      // Refetch accounts and account details to ensure UI is in sync
      refetchAccounts();
      refetchAccountDetails();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      // You might want to show an error toast/notification here
    }
  };

  const seatCap = Number(planConfig.users);
  const seatsUsed = accountDetails?.users?.length ?? 0;
  const seats = Number.isFinite(seatCap) && seatCap > 0 ? `${seatsUsed} of ${seatCap}` : '—';
  const cards = billingData?.billingDetails || [];

  if (loading || accountDetailsLoading) return <Loader label="Loading" className="ink-lede" />;
  if (error || !organization) return <p className="ink-lede">Could not load billing.</p>;

  return (
    <div>
      <h1 className="ink-h1">Plan & billing</h1>
      <div className="ink-bill">
        <div>
          <div className="ink-bill-label">Plan</div>
          <div className="ink-bill-value">{planLabel || '—'}</div>
        </div>
        <div>
          <div className="ink-bill-label">Price</div>
          <div className="ink-bill-value">{priceLabel}</div>
          {billingCycle ? <div className="ink-bill-sub">{billingCycle}</div> : null}
        </div>
        <div>
          <div className="ink-bill-label">Seats</div>
          <div className="ink-bill-value">{seats}</div>
        </div>
        <div>
          <div className="ink-bill-label">
            {isFreePlan
              ? 'Next bill'
              : isCancelled
                ? 'Moves to Free'
                : isTrialing
                  ? 'Trial ends'
                  : 'Next bill'}
          </div>
          <div className="ink-bill-value">
            {isFreePlan
              ? 'No charge'
              : isGrandfathered
                ? 'Not billed'
                : isCancelled && activeUntilDate
                  ? activeUntilDate
                  : isTrialing
                    ? formatDate(billing?.trialEnd || undefined)
                    : nextBillingDate}
          </div>
        </div>
      </div>
      {isPastDue ? (
        <p className="ink-bill-note" data-tone="warn">
          Your last payment didn’t go through. Stripe will try again over the next few days; update
          your card below to keep {planLabel}. If it still fails, your site moves to Free (it stays
          online).
        </p>
      ) : isTrialing && !isCancelled ? (
        <p className="ink-bill-note">
          Your free trial ends on {formatDate(billing?.trialEnd || undefined)}. Your card is charged
          then{billingCycle ? ` (${billingCycle.toLowerCase()})` : ''} unless you move to Free
          first.
        </p>
      ) : isGrandfathered ? (
        <p className="ink-bill-note">
          Your plan started before billing did, so it isn’t charged. Choosing a paid plan starts
          billing.
        </p>
      ) : null}
      <div className="ink-bill-actions">
        <button type="button" className="ink-btn" onClick={handleOpenChangePlan}>
          {isCancelled ? 'Keep my plan' : 'Change plan'}
        </button>
        {!isCancelled && !isFreePlan ? (
          <button
            type="button"
            className="ink-bill-cancel"
            onClick={() => setIsCancelSubscriptionOpen(true)}
          >
            Cancel subscription
          </button>
        ) : null}
      </div>

      <h2 className="ink-bill-h">Payment method</h2>
      {cards.length === 0 ? (
        <div className="ink-bill-pay">
          <span style={{ font: '400 14px/1.4 var(--ink-font)', color: 'var(--ink-ink2)' }}>
            No card on file
          </span>
          <button
            type="button"
            className="ink-bill-link"
            onClick={() => setIsAddPaymentMethodOpen(true)}
          >
            Add card
          </button>
        </div>
      ) : (
        cards.map((detail) => (
          <div className="ink-bill-pay" data-card="true" key={detail.id}>
            <span style={{ font: '400 14px/1.4 var(--ink-font)' }}>
              •••• {detail.cardLast4}
              <span style={{ color: 'var(--ink-ink3)' }}>
                {' '}
                · Expires {detail.cardExpMonth}/{detail.cardExpYear}
                {detail.isDefault ? ' · Default' : ''}
              </span>
            </span>
            <span style={{ display: 'flex', gap: 16 }}>
              {!detail.isDefault ? (
                <button
                  type="button"
                  className="ink-bill-link"
                  onClick={() => handleSetDefaultPaymentMethod(detail.id)}
                  disabled={isSettingDefault}
                >
                  {isSettingDefault ? 'Setting…' : 'Set default'}
                </button>
              ) : null}
              {detail.isRemovable && !detail.isDefault ? (
                <button
                  type="button"
                  className="ink-bill-link"
                  data-muted="true"
                  onClick={() => handleRemovePaymentMethod(detail.id)}
                  disabled={isRemoving}
                >
                  {isRemoving ? 'Removing…' : 'Remove'}
                </button>
              ) : null}
            </span>
          </div>
        ))
      )}
      {cards.length > 0 ? (
        <button
          type="button"
          className="ink-bill-link"
          style={{ marginTop: 12 }}
          onClick={() => setIsAddPaymentMethodOpen(true)}
        >
          Add card
        </button>
      ) : null}

      <h2 className="ink-bill-h">Invoices &amp; receipts</h2>
      <div className="ink-bill-pay">
        <span style={{ font: '400 14px/1.4 var(--ink-font)', color: 'var(--ink-ink2)' }}>
          Past invoices, receipts and billing details, on Stripe’s secure page.
        </span>
        <button
          type="button"
          className="ink-bill-link"
          onClick={() => void goToPortal()}
          disabled={openingPortal}
        >
          {openingPortal ? 'Opening…' : 'View invoices'}
        </button>
      </div>
      {portalError ? <p className="cp-error">{portalError}</p> : null}

      {/* Add Payment Method Modal */}
      <Dialog open={isAddPaymentMethodOpen} onOpenChange={setIsAddPaymentMethodOpen}>
        <DialogContent className="sm:max-w-md bg-core-dark">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Add Payment Method
            </DialogTitle>
            <DialogDescription>
              Add a new payment method to your account. Your payment information is secure and
              encrypted.
            </DialogDescription>
          </DialogHeader>
          {accountId && (
            <Elements stripe={stripePromise}>
              <PaymentForm
                accountId={accountId}
                userEmail={userProfileData?.me?.email}
                onSuccess={handleAddPaymentMethodSuccess}
                onCancel={() => setIsAddPaymentMethodOpen(false)}
              />
            </Elements>
          )}
        </DialogContent>
      </Dialog>

      {/* Change plan: TinyCMS has its own (UX review v4); other products keep the dialog. */}
      {isTinycms && isChangePlanOpen ? (
        <TinycmsChangePlan
          plans={tinycmsPlans}
          currentKey={tinycmsPlan?.key || 'free'}
          currentInterval={tinycmsPlan ? orderInterval : 'year'}
          cardLast4={(cards.find((c) => c.isDefault) || cards[0])?.cardLast4 || null}
          billing={billing}
          saving={isUpdatingPlan}
          error={planError}
          onConfirm={handleSwitchTinycmsPlan}
          onAddCard={() => {
            setIsChangePlanOpen(false);
            setIsAddPaymentMethodOpen(true);
          }}
          onClose={() => setIsChangePlanOpen(false)}
        />
      ) : null}
      <Dialog open={!isTinycms && isChangePlanOpen} onOpenChange={setIsChangePlanOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto bg-core-dark">
          <DialogHeader>
            <DialogTitle>Change Plan</DialogTitle>
            <DialogDescription>
              Select a new plan for your account. Changes will take effect immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Label className="mb-4 block">Select Your Plan</Label>
            <RadioGroup value={selectedPlan} onValueChange={(value) => setSelectedPlan(value)}>
              <div className="grid md:grid-cols-3 gap-6">
                {pricingPlans.map((plan) => (
                  <label
                    key={plan.id}
                    className={`relative cursor-pointer rounded-lg border transition-all ${
                      selectedPlan === plan.id
                        ? 'border-primary ring-2 ring-primary ring-offset-2'
                        : 'hover:border-primary/50'
                    }`}
                  >
                    <Card className="border-0">
                      <CardHeader>
                        <div className="flex items-center justify-between mb-2">
                          <RadioGroupItem value={plan.id} id={plan.id} />
                          {plan.popular && (
                            <span className="px-3 py-1 bg-primary text-primary-foreground rounded-full text-xs">
                              Most Popular
                            </span>
                          )}
                        </div>
                        <CardTitle>{plan.name}</CardTitle>
                        <div className="mt-4">
                          <span className="font-bold">${plan.price}</span>
                          <span className="text-muted-foreground">/month</span>
                        </div>
                        <CardDescription>{plan.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-3">
                          {plan.features.map((feature) => (
                            <li key={feature} className="flex items-start gap-2">
                              <Check className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                              <span className="text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </label>
                ))}
              </div>
            </RadioGroup>
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => setIsChangePlanOpen(false)}
              disabled={isUpdatingPlan}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSavePlan} disabled={isUpdatingPlan}>
              {isUpdatingPlan ? 'Saving...' : 'Save Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Confirmation Modal */}
      <Dialog open={isCancelSubscriptionOpen} onOpenChange={setIsCancelSubscriptionOpen}>
        <DialogContent className="sm:max-w-md bg-core-dark">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Cancel Subscription
            </DialogTitle>
            <DialogDescription>
              {isGrandfathered
                ? 'Your site moves to the Free plan now. It stays online, with the “powered by tinycms.ai” bar.'
                : `You keep ${planLabel} until ${activeUntilDate || nextBillingDate}, and you won’t be charged again. Then your site moves to the Free plan — it stays online, with the “powered by tinycms.ai” bar. You can keep your plan any time before then.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => setIsCancelSubscriptionOpen(false)}
              disabled={isCancelingSubscription}
              className="bg-button-primary text-button-primary"
            >
              Keep Subscription
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={isCancelingSubscription}
              className="bg-button-secondary text-button-secondary"
            >
              {isCancelingSubscription ? 'Canceling...' : 'Cancel Subscription'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
